import { Button, Loader, Modal } from "@marketplace/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  PlatformFeePackageItem,
  UpdatePlatformFeeRequestDto
} from "@/modules/product/dtos/update-platform-fee-dto";

import { usePutPlatformFeeMutation } from "../../hooks/mutations/use-put-set-platform-fee-mutation";
import type { PlatformFeeData } from "../platform-fee-input-modal/platform-fee-input-modal";

type PlatformFeeSummaryModalProps = {
  opened: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
  onBack: () => void;
  packages: PlatformFeeData[];
  isEditMode?: boolean;
};

export const PlatformFeeSummaryModal = ({
  opened,
  onClose,
  onSaveSuccess,
  onBack,
  packages,
  isEditMode
}: PlatformFeeSummaryModalProps) => {
  const { t: tCommon } = useTranslation("common");
  const { t: tProduct } = useTranslation("product");
  const [showError, setShowError] = useState(false);

  const summaryPackages = packages.filter((pkg) => !pkg.isFreeTrial);

  const formatMarginValue = (data: PlatformFeeData): string => {
    if (data.targetMarginType === "percentage") {
      return `${data.percentageValue}%`;
    }
    return `Rp ${Number(data.nominalValue).toLocaleString("id-ID")}`;
  };

  const transformToApiPayload = (): UpdatePlatformFeeRequestDto => {
    const platformFeePackages: PlatformFeePackageItem[] = summaryPackages
      .filter((pkg) => pkg.packageId !== undefined)
      .map((pkg) => ({
        package_id: pkg.packageId as number,
        platform_fee_type: pkg.targetMarginType,
        platform_fee_value:
          pkg.targetMarginType === "percentage"
            ? Number(pkg.percentageValue)
            : Number(pkg.nominalValue)
      }));

    return { packages: platformFeePackages };
  };

  const mutation = usePutPlatformFeeMutation();

  const handleSave = () => {
    const payload = transformToApiPayload();
    mutation.mutate(payload, {
      onSuccess: () => {
        onSaveSuccess();
      },
      onError: () => {
        setShowError(true);
      }
    });
  };

  const handleClose = () => {
    setShowError(false);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Platform Fee "
      zIndex={401}
      size="md"
    >
      <div className="flex flex-col gap-y-4">
        <h3 className="text-h-s text-fjord-grey-500">Summary</h3>
        {showError && (
          <p className=" text-red-500 bg-red-200 p-3 rounded">
            {tProduct("notifications.failedToSavePlatformFee")}
          </p>
        )}
        <div className="flex flex-col gap-y-3">
          {summaryPackages.length === 0 ? (
            <div className="text-center py-8 text-fjord-grey-500">
              <p>No platform fee data available</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-x-8 pt-4">
                <span className="text-sm text-fjord-grey-500">
                  Package Name
                </span>
                <span className="text-sm text-fjord-grey-500">
                  Target Margin
                </span>
              </div>

              {summaryPackages.map((pkg, index) => (
                <div
                  key={pkg.packageId ?? index}
                  className="grid grid-cols-2 gap-x-8"
                >
                  <span className="font-medium text-fjord-grey-700">
                    {pkg.packageName || `Package ${index + 1}`}
                  </span>
                  <span className="font-medium text-fjord-grey-700">
                    {formatMarginValue(pkg)}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="flex justify-end items-center gap-x-[14px] pt-4 border-t">
          <Button variant="secondary" className="w-[128px]" onClick={onBack}>
            Back
          </Button>
          {isEditMode ? (
            <Button
              variant="primary"
              className="w-[128px]"
              onClick={handleSave}
              disabled={mutation.isPending || mutation.isError}
              leftSection={mutation.isPending ? <Loader size={16} /> : null}
            >
              {mutation.isPending ? "updating..." : tCommon("update")}
            </Button>
          ) : (
            <Button
              variant="primary"
              className="w-[128px]"
              onClick={handleSave}
              disabled={mutation.isPending || mutation.isError}
              leftSection={mutation.isPending ? <Loader size={16} /> : null}
            >
              {mutation.isPending ? "saving..." : tCommon("save")}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
